import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
type PageHeroProps = {
  eyebrow?: string;
  title: React.ReactNode;
  /** Small uppercase line above the title (e.g. "DiligenceGPT for Venture Capital"). */
  kicker?: string;
  description?: React.ReactNode;
  breadcrumb: string;
  /** Optional middle breadcrumb level, e.g. Solutions. */
  breadcrumbParent?: {
    label: string;
    href: string;
  };
  actions?: React.ReactNode;
  aside?: React.ReactNode;
  headingId: string;
};

/** Dark hero used by the platform pages. */
export function PageHero({
  eyebrow,
  kicker,
  title,
  description,
  breadcrumb,
  breadcrumbParent,
  actions,
  aside,
  headingId
}: PageHeroProps) {
  const reduceMotion = useReducedMotion();
  const fade = (delay: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      delay,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  };
  return <section aria-labelledby={headingId} className="dgpt-dark relative overflow-hidden bg-night" data-id="element-1198" data-mp="279k8v0w37y62">
      <div className="dgpt-night-grid sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-1199" data-mp="2l0yqp4e9dtbj" />
      <div className="relative mx-auto w-full max-w-[1320px] px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pb-24 lg:pt-10" data-id="element-1200" data-mp="2gaibpfushzn2">
        <nav aria-label="Breadcrumb" data-id="element-1201" data-mp="2uyd75406mi3u">
          <ol className="flex items-center gap-1.5 text-xs text-night-faint" data-id="element-1202" data-mp="2uxbitxu5nz46">
            <li data-id="element-1203" data-mp="2pz9yzaownvrn">
              <Link to="/" className="hover:text-night-text" data-id="element-1204" data-mp="2pzs4kxxgjmc8">Home</Link>
            </li>
            <li aria-hidden="true" data-id="element-1205" data-mp="33zb08r93157n"><ChevronRightIcon className="h-3 w-3" data-id="element-1206" data-mp="33xqglwob36ok" /></li>
            {breadcrumbParent ? <>
                <li data-id="element-1207" data-mp="2yyx529skxpr1">
                  <Link to={breadcrumbParent.href} className="hover:text-night-text" data-id="element-1208" data-mp="19ljxmbp67llp">{breadcrumbParent.label}</Link>
                </li>
                <li aria-hidden="true" data-id="element-1209" data-mp="14o0eoyhj2knf"><ChevronRightIcon className="h-3 w-3" data-id="element-1210" data-mp="14ojqdxgl4ct0" /></li>
              </> : null}
            <li aria-current="page" className="text-night-muted" data-id="element-1211" data-mp="34qsx9c883z7q">{breadcrumb}</li>
          </ol>
        </nav>
        <div className={aside ? 'mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center' : 'mt-12'} data-id="element-1212" data-mp="39q5m22inngcy">
          <div className="flex max-w-4xl flex-col items-start" data-id="element-1213" data-mp="3iohkgqprjify">
            {eyebrow ? <motion.p {...fade(0)} className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-night-muted" data-id="element-1214" data-mp="3nnu472lc89eb">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-violet" data-id="element-1215" data-mp="3slm5uobsb9vp" />
                {eyebrow}
              </motion.p> : null}
            {kicker ? <motion.p {...fade(0.03)} className="mt-5 text-sm font-semibold uppercase tracking-[0.1em] text-violet-ink" data-id="element-1216" data-mp="66ayloklkb2n">
                {kicker}
              </motion.p> : null}
            <motion.h1 {...fade(0.06)} id={headingId} className="sf-display on-dark mt-5 text-balance text-[38px] leading-[1.02] text-night-text sm:text-[54px] lg:text-[64px]" data-id="element-1217" data-mp="f92crw4uxkxz">
              {title}
            </motion.h1>
            {description ? <motion.p {...fade(0.1)} className="mt-6 max-w-2xl text-base leading-relaxed text-night-muted sm:text-lg" data-id="element-1218" data-mp="1qq6b53go7e0m">
                {description}
              </motion.p> : null}
            {actions ? <motion.div {...fade(0.14)} className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row" data-id="element-1219" data-mp="2ifrjg21go6vq">
                {actions}
              </motion.div> : null}
          </div>
          {aside ? <motion.div {...fade(0.16)} className="min-w-0" data-id="element-1220" data-mp="29m4ryo4adpsc">{aside}</motion.div> : null}
        </div>
      </div>
    </section>;
}